import { useMemo, useState } from 'react';
import { Search, LayoutGrid, List, SlidersHorizontal } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import type {
  CoverageItem,
  MediaClass,
  MediaItemStatus,
  MediaKind,
  MediaOutlet,
  MediaSentiment,
  TraditionalMedium,
} from '@/client/types/media';
import {
  MEDIA_CLASS_BADGE,
  MEDIA_SENTIMENT_BADGE,
  MEDIA_STATUS_BADGE,
  MEDIUM_LABEL,
  publicMetrics,
} from '@/client/types/media';
import { formatCompact } from '../fixtures';
import {
  publishedRangeParams,
  useAdaptedOutlets,
  useApiTrackedKeywords,
  useCoverageItems,
} from '../coverage-api';
import { useBulkTriageMutation } from '@/client/api/marketing/radar';
import { getApiErrorMessage } from '@/client/utils/errors';
import { CoverageDetailDrawer } from './CoverageDetailDrawer';
import { OutletFavicon } from './OutletFavicon';
import { KeywordTrackerPanel } from './KeywordTrackerPanel';
import { matchingKeywords } from '../keyword-match';
import { INSIGHT_RANGE_DAYS, type InsightRangeDays } from '@/client/pages/Admin/Marketing/posting-insights-data';

const MEDIUMS: TraditionalMedium[] = [
  'print',
  'online_news',
  'tv',
  'radio',
  'podcast',
  'wire',
  'other',
];

type ViewMode = 'list' | 'table';
type SortKey = 'urgency' | 'published' | 'reach';

interface TraditionalCoverageViewProps {
  kind: MediaKind | MediaKind[];
  defaultStatus?: MediaItemStatus | 'all';
  /** Show keyword tracker + filter. Defaults to true. */
  enableKeywordTracking?: boolean;
}

interface OutletOption {
  outlet: MediaOutlet;
  count: number;
}

function matchesKind(item: CoverageItem, kind: MediaKind | MediaKind[]) {
  return Array.isArray(kind) ? kind.includes(item.kind) : item.kind === kind;
}

export function TraditionalCoverageView({
  kind,
  defaultStatus = 'all',
  enableKeywordTracking = true,
}: TraditionalCoverageViewProps) {
  const [rangeDays, setRangeDays] = useState<InsightRangeDays>(90);
  const windowParams = useMemo(() => publishedRangeParams(rangeDays), [rangeDays]);
  const coverage = useCoverageItems({
    channel: 'traditional',
    ...windowParams,
  });
  const items = coverage.items;
  const outlets = useAdaptedOutlets();
  const bulkTriage = useBulkTriageMutation();
  const { keywords: trackedKeywords } = useApiTrackedKeywords();
  const channelKeywords = useMemo(
    () => trackedKeywords.filter((k) => k.channels.includes('traditional')),
    [trackedKeywords],
  );

  const [q, setQ] = useState('');
  const [view, setView] = useState<ViewMode>('table');
  const [status, setStatus] = useState<MediaItemStatus | 'all'>(defaultStatus);
  const [mediums, setMediums] = useState<Set<TraditionalMedium>>(new Set());
  const [outletIds, setOutletIds] = useState<Set<string>>(new Set());
  const [keywordIds, setKeywordIds] = useState<Set<string>>(new Set());
  const [mediaClass, setMediaClass] = useState<MediaClass | 'all'>('all');
  const [sentiment, setSentiment] = useState<MediaSentiment | 'all'>('all');
  const [sort, setSort] = useState<SortKey>('urgency');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [active, setActive] = useState<CoverageItem | null>(null);

  const traditionalBase = useMemo(
    () => items.filter((i) => i.channel === 'traditional' && matchesKind(i, kind)),
    [items, kind],
  );

  const mediumCounts = useMemo(() => {
    const counts: Partial<Record<TraditionalMedium, number>> = {};
    for (const item of traditionalBase) {
      const outlet = outlets.find((o) => o.id === item.outletId);
      const medium = outlet?.medium;
      if (!medium) continue;
      counts[medium] = (counts[medium] ?? 0) + 1;
    }
    return counts;
  }, [traditionalBase, outlets]);

  const outletOptions = useMemo(() => {
    const counts = new Map<string, number>();
    for (const item of traditionalBase) {
      if (!item.outletId) continue;
      const outlet = outlets.find((o) => o.id === item.outletId);
      if (!outlet) continue;
      if (mediums.size > 0 && !mediums.has(outlet.medium)) continue;
      counts.set(item.outletId, (counts.get(item.outletId) ?? 0) + 1);
    }

    const opts: OutletOption[] = [];
    for (const [id, count] of counts) {
      const outlet = outlets.find((o) => o.id === id);
      if (!outlet) continue;
      opts.push({ outlet, count });
    }

    for (const outlet of outlets) {
      if (mediums.size > 0 && !mediums.has(outlet.medium)) continue;
      if (opts.some((o) => o.outlet.id === outlet.id)) continue;
      if (mediums.size > 0) opts.push({ outlet, count: 0 });
    }

    return opts.sort((a, b) => {
      const mi = MEDIUMS.indexOf(a.outlet.medium) - MEDIUMS.indexOf(b.outlet.medium);
      if (mi !== 0) return mi;
      return b.count - a.count;
    });
  }, [traditionalBase, mediums, outlets]);

  const outletsByMedium = useMemo(() => {
    const groups: { medium: TraditionalMedium; options: OutletOption[] }[] = [];
    for (const m of MEDIUMS) {
      const options = outletOptions.filter((o) => o.outlet.medium === m);
      if (options.length) groups.push({ medium: m, options });
    }
    return groups;
  }, [outletOptions]);

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    let list = traditionalBase
      .filter((item) => (status === 'all' ? true : item.status === status))
      .filter((item) => {
        if (mediums.size === 0) return true;
        const outlet = outlets.find((o) => o.id === item.outletId);
        return outlet != null && mediums.has(outlet.medium);
      })
      .filter((item) => {
        if (outletIds.size === 0) return true;
        return item.outletId != null && outletIds.has(item.outletId);
      })
      .filter((item) => {
        if (!enableKeywordTracking || keywordIds.size === 0) return true;
        const selectedKws = channelKeywords.filter((k) => keywordIds.has(k.id) && k.enabled);
        if (selectedKws.length === 0) return true;
        return matchingKeywords(item, selectedKws).length > 0;
      })
      .filter((item) => (mediaClass === 'all' ? true : item.mediaClass === mediaClass))
      .filter((item) => (sentiment === 'all' ? true : item.sentiment === sentiment))
      .filter((item) => {
        if (!query) return true;
        const hay = [
          item.title,
          item.summary,
          item.bodyExcerpt,
          item.author,
          item.outletName,
          ...(item.tags ?? []),
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return hay.includes(query);
      });

    list = [...list].sort((a, b) => {
      if (sort === 'published') return b.publishedAt.localeCompare(a.publishedAt);
      if (sort === 'reach') {
        const br = publicMetrics(b);
        const ar = publicMetrics(a);
        return (br.reach ?? br.views ?? 0) - (ar.reach ?? ar.views ?? 0);
      }
      return (b.urgencyScore ?? -1) - (a.urgencyScore ?? -1);
    });

    return list;
  }, [
    traditionalBase,
    status,
    mediums,
    outletIds,
    outlets,
    enableKeywordTracking,
    keywordIds,
    channelKeywords,
    mediaClass,
    sentiment,
    q,
    sort,
  ]);

  const toggleMedium = (m: TraditionalMedium) => {
    setMediums((prev) => {
      const next = new Set(prev);
      if (next.has(m)) next.delete(m);
      else next.add(m);

      setOutletIds((prevOut) => {
        if (prevOut.size === 0 || next.size === 0) return prevOut;
        const allowed = new Set(
          outlets.filter((o) => next.has(o.medium)).map((o) => o.id),
        );
        return new Set([...prevOut].filter((id) => allowed.has(id)));
      });

      return next;
    });
  };

  const toggleOutlet = (id: string) => {
    setOutletIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAllOutletsOnMedium = (medium: TraditionalMedium) => {
    const ids = outletOptions
      .filter((o) => o.outlet.medium === medium)
      .map((o) => o.outlet.id);
    setOutletIds((prev) => {
      const next = new Set(prev);
      const allSelected = ids.every((id) => next.has(id));
      if (allSelected) ids.forEach((id) => next.delete(id));
      else ids.forEach((id) => next.add(id));
      return next;
    });
    setMediums((prev) => new Set(prev).add(medium));
  };

  const bulk = (next: MediaItemStatus) => {
    const ids = [...selected];
    if (ids.length === 0) return;
    bulkTriage.mutate(
      { ids, status: next },
      {
        onSuccess: (res) =>
          toast.success(`${res?.data?.modified ?? 0} of ${ids.length} moved to ${next}`),
        onError: (err) => toast.error(getApiErrorMessage(err, 'Could not triage the selection')),
      },
    );
    setSelected(new Set());
  };

  const liveItems = filtered.map((f) => items.find((i) => i.id === f.id) ?? f);

  const toggleSelect = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div className="space-y-4">
      {enableKeywordTracking && (
        <KeywordTrackerPanel
          items={traditionalBase}
          selectedIds={keywordIds}
          onSelectedIdsChange={setKeywordIds}
          channel="traditional"
        />
      )}

      <div className="rounded-xl border border-gray-100 bg-white p-3 shadow-sm">
        <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
          Mediums
        </p>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => {
              setMediums(new Set());
              setOutletIds(new Set());
            }}
            className={`rounded-full border px-3 py-1.5 text-xs font-medium transition ${
              mediums.size === 0
                ? 'border-blue-200 bg-blue-50 text-blue-800'
                : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
            }`}
          >
            All · {traditionalBase.length}
          </button>
          {MEDIUMS.map((m) => {
            const count = mediumCounts[m] ?? 0;
            if (count === 0) return null;
            const activeChip = mediums.has(m);
            return (
              <button
                key={m}
                type="button"
                onClick={() => toggleMedium(m)}
                className={`inline-flex items-center gap-2 rounded-full border py-1.5 px-3 text-xs font-medium transition ${
                  activeChip
                    ? 'border-blue-300 bg-blue-50 text-blue-900 ring-2 ring-blue-200 ring-offset-1'
                    : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                <span>{MEDIUM_LABEL[m]}</span>
                <span className="rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] text-gray-600">
                  {count}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="rounded-xl border border-gray-100 bg-white p-3 shadow-sm">
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">
            Outlets
            {mediums.size > 0 && (
              <span className="ml-1 font-normal normal-case text-gray-400">
                · filtered to selected mediums
              </span>
            )}
          </p>
          {outletIds.size > 0 && (
            <button
              type="button"
              onClick={() => setOutletIds(new Set())}
              className="text-[11px] font-medium text-blue-700 hover:underline"
            >
              Clear outlets
            </button>
          )}
        </div>

        {outletsByMedium.length === 0 ? (
          <p className="text-sm text-gray-500">No outlets match the current medium filters.</p>
        ) : (
          <div className="space-y-3">
            {outletsByMedium.map(({ medium, options }) => (
              <div key={medium}>
                <div className="mb-1.5 flex items-center gap-2">
                  <span className="text-xs font-semibold text-gray-800">
                    {MEDIUM_LABEL[medium]}
                  </span>
                  <button
                    type="button"
                    onClick={() => selectAllOutletsOnMedium(medium)}
                    className="text-[11px] font-medium text-blue-700 hover:underline"
                  >
                    Toggle all
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {options.map(({ outlet, count }) => {
                    const activeOut = outletIds.has(outlet.id);
                    return (
                      <button
                        key={outlet.id}
                        type="button"
                        onClick={() => toggleOutlet(outlet.id)}
                        className={`inline-flex items-center gap-2 rounded-full border py-1 pl-1 pr-2.5 text-left text-xs transition ${
                          activeOut
                            ? 'border-blue-300 bg-blue-50 ring-2 ring-blue-200 ring-offset-1'
                            : 'border-gray-200 bg-white hover:bg-gray-50'
                        }`}
                      >
                        <OutletFavicon
                          name={outlet.name}
                          website={outlet.website}
                          size="sm"
                          className="rounded-full"
                        />
                        <span className="min-w-0">
                          <span className="flex items-center gap-1 font-medium text-gray-900">
                            {outlet.name}
                            {outlet.doNotPitch && (
                              <span className="rounded bg-red-50 px-1 py-px text-[9px] font-medium uppercase text-red-700">
                                Do not pitch
                              </span>
                            )}
                          </span>
                          <span className="block truncate text-[10px] text-gray-500">
                            T{outlet.tier} · {count}
                          </span>
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2 rounded-xl border border-gray-100 bg-white p-3 shadow-sm">
        <div className="relative min-w-[180px] flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search title, outlet, author, body…"
            className="h-9 w-full rounded-md border border-gray-200 bg-white pl-9 pr-3 text-sm outline-none ring-blue-500 focus:ring-2"
          />
        </div>
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value as MediaItemStatus | 'all')}
          className="h-9 rounded-md border border-gray-200 px-2 text-sm"
        >
          <option value="all">All statuses</option>
          <option value="new">New</option>
          <option value="reviewed">Reviewed</option>
          <option value="flagged">Flagged</option>
          <option value="archived">Archived</option>
        </select>
        <select
          value={String(rangeDays)}
          onChange={(e) => setRangeDays(Number(e.target.value) as InsightRangeDays)}
          className="h-9 rounded-md border border-gray-200 px-2 text-sm"
          title="Server-side published_at window"
        >
          {INSIGHT_RANGE_DAYS.map((d) => (
            <option key={d} value={d}>
              Last {d} days
            </option>
          ))}
        </select>
        <select
          value={mediaClass}
          onChange={(e) => setMediaClass(e.target.value as MediaClass | 'all')}
          className="h-9 rounded-md border border-gray-200 px-2 text-sm"
        >
          <option value="all">All classes</option>
          <option value="owned">Owned</option>
          <option value="earned">Earned</option>
          <option value="shared">Shared / UGC</option>
          <option value="paid">Paid</option>
        </select>
        <select
          value={sentiment}
          onChange={(e) => setSentiment(e.target.value as MediaSentiment | 'all')}
          className="h-9 rounded-md border border-gray-200 px-2 text-sm"
        >
          <option value="all">All sentiment</option>
          <option value="positive">Positive</option>
          <option value="neutral">Neutral</option>
          <option value="negative">Negative</option>
        </select>
        <select
          value={sort}
          onChange={(e) => setSort(e.target.value as SortKey)}
          className="h-9 rounded-md border border-gray-200 px-2 text-sm"
        >
          <option value="urgency">Sort: urgency</option>
          <option value="published">Sort: newest</option>
          <option value="reach">Sort: reach</option>
        </select>
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="gap-1"
          onClick={() => setShowAdvanced((v) => !v)}
        >
          <SlidersHorizontal className="h-3.5 w-3.5" />
          More
        </Button>
        <div className="ml-auto flex rounded-md border border-gray-200 p-0.5">
          <button
            type="button"
            onClick={() => setView('list')}
            className={`inline-flex items-center gap-1 rounded px-2.5 py-1.5 text-xs font-medium ${
              view === 'list' ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            <LayoutGrid className="h-3.5 w-3.5" /> List
          </button>
          <button
            type="button"
            onClick={() => setView('table')}
            className={`inline-flex items-center gap-1 rounded px-2.5 py-1.5 text-xs font-medium ${
              view === 'table' ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            <List className="h-3.5 w-3.5" /> Table
          </button>
        </div>
      </div>

      {showAdvanced && (
        <div className="flex flex-wrap items-center gap-3 rounded-xl border border-dashed border-gray-200 bg-gray-50 px-4 py-3 text-sm">
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={() => {
              setMediums(new Set());
              setOutletIds(new Set());
              setMediaClass('all');
              setSentiment('all');
              setStatus(defaultStatus);
              setQ('');
              setKeywordIds(new Set());
            }}
          >
            Reset filters
          </Button>
        </div>
      )}

      {selected.size > 0 && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-blue-100 bg-blue-50 px-3 py-2 text-sm text-blue-900">
          <span className="font-medium">{selected.size} selected</span>
          <Button size="sm" variant="secondary" onClick={() => bulk('reviewed')}>
            Mark reviewed
          </Button>
          <Button size="sm" variant="secondary" onClick={() => bulk('flagged')}>
            Flag
          </Button>
          <Button size="sm" variant="secondary" onClick={() => bulk('archived')}>
            Archive
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())}>
            Clear
          </Button>
        </div>
      )}

      <p className="text-xs text-gray-500">
        Showing <span className="font-medium text-gray-800">{liveItems.length}</span> of{' '}
        {traditionalBase.length} traditional items
        {outletIds.size > 0 && (
          <span>
            {' '}
            · {outletIds.size} outlet{outletIds.size === 1 ? '' : 's'} selected
          </span>
        )}
        {enableKeywordTracking && keywordIds.size > 0 && (
          <span>
            {' '}
            · {keywordIds.size} keyword tracker{keywordIds.size === 1 ? '' : 's'}
          </span>
        )}
      </p>

      {liveItems.length === 0 && (
        <div className="rounded-xl border border-gray-100 bg-white py-16 text-center text-sm text-gray-500 shadow-sm">
          No traditional items match these filters.
        </div>
      )}

      {view === 'list' && liveItems.length > 0 && (
        <div className="space-y-2">
          {liveItems.map((item) => {
            const outlet = outlets.find((o) => o.id === item.outletId);
            return (
              <div
                key={item.id}
                className="flex items-start gap-3 rounded-xl border border-gray-100 bg-white p-3 shadow-sm transition hover:bg-gray-50"
              >
                <input
                  type="checkbox"
                  checked={selected.has(item.id)}
                  onChange={() => toggleSelect(item.id)}
                  className="mt-1 h-4 w-4 rounded border-gray-300"
                  aria-label={`Select ${item.title}`}
                />
                <button
                  type="button"
                  className="min-w-0 flex-1 text-left"
                  onClick={() => setActive(item)}
                >
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-medium text-gray-900">{item.title}</span>
                    <span
                      className={`rounded-full px-2 py-0.5 text-[10px] font-medium capitalize ${MEDIA_STATUS_BADGE[item.status]}`}
                    >
                      {item.status}
                    </span>
                    <span
                      className={`rounded-full px-2 py-0.5 text-[10px] font-medium capitalize ${MEDIA_SENTIMENT_BADGE[item.sentiment]}`}
                    >
                      {item.sentiment}
                    </span>
                  </div>
                  <p className="mt-0.5 line-clamp-2 text-xs text-gray-500">
                    {item.summary || item.bodyExcerpt}
                  </p>
                  <div className="mt-1.5 flex flex-wrap items-center gap-1.5 text-[11px] text-gray-500">
                    <span className="inline-flex items-center gap-1.5 font-medium text-gray-700">
                      <OutletFavicon
                        name={outlet?.name ?? item.outletName ?? 'Outlet'}
                        website={outlet?.website}
                        size="xs"
                        className="rounded"
                      />
                      {outlet?.name ?? item.outletName ?? 'Unknown outlet'}
                    </span>
                    {outlet && (
                      <span className="rounded bg-gray-100 px-1.5 py-0.5 text-gray-600">
                        {MEDIUM_LABEL[outlet.medium]}
                      </span>
                    )}
                    <span>· urg {item.urgencyScore ?? '—'}</span>
                    <span>· reach {formatCompact(publicMetrics(item).reach ?? publicMetrics(item).views)}</span>
                    {enableKeywordTracking &&
                      matchingKeywords(
                        item,
                        channelKeywords.filter((k) => k.enabled),
                      )
                        .slice(0, 3)
                        .map((kw) => (
                          <span
                            key={kw.id}
                            className="rounded-full bg-violet-50 px-2 py-0.5 text-[10px] font-medium text-violet-800"
                          >
                            {kw.label}
                          </span>
                        ))}
                  </div>
                </button>
              </div>
            );
          })}
        </div>
      )}

      {view === 'table' && liveItems.length > 0 && (
        <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="w-10 px-3 py-3" />
                  <th className="px-3 py-3 text-left text-xs font-medium uppercase text-gray-500">
                    Coverage
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium uppercase text-gray-500">
                    Outlet
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium uppercase text-gray-500">
                    Reach
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium uppercase text-gray-500">
                    Tracking
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium uppercase text-gray-500">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {liveItems.map((item) => {
                  const outlet = outlets.find((o) => o.id === item.outletId);
                  const matched =
                    enableKeywordTracking
                      ? matchingKeywords(
                          item,
                          channelKeywords.filter((k) => k.enabled),
                        ).slice(0, 3)
                      : [];
                  return (
                    <tr
                      key={item.id}
                      className="cursor-pointer hover:bg-gray-50"
                      onClick={() => setActive(item)}
                    >
                      <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selected.has(item.id)}
                          onChange={() => toggleSelect(item.id)}
                        />
                      </td>
                      <td className="px-3 py-3">
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium text-gray-900">{item.title}</p>
                          <p className="mt-0.5 line-clamp-1 text-xs text-gray-400">
                            {item.summary || item.bodyExcerpt}
                          </p>
                        </div>
                      </td>
                      <td className="px-3 py-3">
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {outlet?.name ?? item.outletName ?? '—'}
                          </p>
                          <p className="text-[11px] text-gray-500">
                            {outlet ? MEDIUM_LABEL[outlet.medium] : '—'}
                            {outlet?.doNotPitch ? ' · do not pitch' : ''}
                          </p>
                        </div>
                      </td>
                      <td className="px-3 py-3 text-xs text-gray-600">
                        <div>{formatCompact(publicMetrics(item).reach ?? publicMetrics(item).views)}</div>
                        <div>
                          {publicMetrics(item).prValue != null
                            ? `PR $${formatCompact(publicMetrics(item).prValue)}`
                            : '—'}
                        </div>
                      </td>
                      <td className="px-3 py-3">
                        <div className="flex flex-wrap gap-1">
                          {matched.length === 0 ? (
                            <span className="text-xs text-gray-400">—</span>
                          ) : (
                            matched.map((kw) => (
                              <span
                                key={kw.id}
                                className="rounded-full bg-violet-50 px-2 py-0.5 text-[10px] font-medium text-violet-800"
                              >
                                {kw.label}
                              </span>
                            ))
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-3">
                        <span
                          className={`rounded-full px-2 py-0.5 text-xs font-medium capitalize ${MEDIA_STATUS_BADGE[item.status]}`}
                        >
                          {item.status}
                        </span>
                        <div className="mt-1">
                          <span
                            className={`rounded-full px-2 py-0.5 text-[10px] font-medium capitalize ${MEDIA_CLASS_BADGE[item.mediaClass]}`}
                          >
                            {item.mediaClass}
                          </span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <CoverageDetailDrawer
        item={active ? items.find((i) => i.id === active.id) ?? active : null}
        open={!!active}
        onClose={() => setActive(null)}
      />
    </div>
  );
}
